import { ColumnDef } from '@tanstack/react-table'
import { com } from '@eidu/web'
import { iconSizes, Tooltip } from '@common/ui'
import { IconCircleCheck, IconExclamationCircle } from '@common/ui/icons'
import React from 'react'
import FieldData from '../../domain/entity/FieldData'
import PaginationModel from '../../util/pagination/PaginationModel'
import { BatchFieldErrors } from '../../domain/entity/FieldValueUtil'
import EntityWithLabel from '../../domain/entity/EntityWithLabel'
import getRenderFieldCell, { CellContent, cellContentComparator } from '../misc/getRenderFieldCell'
import PagedTable from './PagedTable'
import EntityId = com.eidu.entity.EntityId
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType

type UploadRow = Record<string, CellContent | number | boolean | undefined>

const renderValidationCell = (invalid: boolean) =>
  invalid ? (
    <Tooltip label="Validation failed">
      <IconExclamationCircle
        size={iconSizes.sm}
        stroke={1.5}
        color="var(--trellis-color-negative)"
        style={{ marginInline: 8 }}
      />
    </Tooltip>
  ) : (
    <Tooltip label="Validated">
      <IconCircleCheck
        size={iconSizes.sm}
        stroke={1.5}
        color="var(--trellis-color-positive)"
        style={{ marginInline: 8 }}
      />
    </Tooltip>
  )

const getColumns = (
  fields: readonly FieldData[],
  relatedEntities: ReadonlyMap<EntityId, EntityWithLabel | null>,
  relatedTypes: ReadonlyMap<EntityTypeId, EntityType>
): ColumnDef<UploadRow, unknown>[] => [
  { accessorKey: 'id', header: 'Row' },
  {
    accessorKey: 'validation',
    header: 'Validation',
    cell: ({ getValue }) => renderValidationCell(!!getValue()),
  },
  ...fields.map(
    ({ field }): ColumnDef<UploadRow, unknown> => ({
      accessorKey: `field-${field.id.toString()}`,
      header: field.name,
      sortingFn: (rowA, rowB, columnId) =>
        cellContentComparator(
          (rowA.getValue(columnId) as CellContent | undefined) ?? {},
          (rowB.getValue(columnId) as CellContent | undefined) ?? {}
        ),
      cell: ({ getValue }) =>
        getRenderFieldCell(field.type, relatedEntities, relatedTypes)({ value: getValue() as CellContent }),
    })
  ),
]

const getRows = (
  data: readonly (readonly string[])[],
  fields: readonly FieldData[],
  validationErrors: BatchFieldErrors | undefined
): UploadRow[] =>
  data.map((row, index) =>
    Object.fromEntries([
      ['id', index + 1],
      ['validation', validationErrors && validationErrors[index] && validationErrors[index].size > 0],
      ...fields.map(({ field }, colIndex) => [
        `field-${field.id.toString()}`,
        {
          value: row[colIndex],
          error: validationErrors?.[index]
            ?.get(field.id)
            ?.map((it) => it.message)
            .join(', '),
        },
      ]),
    ])
  )

type EntityDataGridParams = {
  data: readonly (readonly string[])[]
  fields: readonly FieldData[]
  referencedEntities: ReadonlyMap<EntityId, EntityWithLabel | null>
  referencedTypes: ReadonlyMap<EntityTypeId, EntityType>
  paginationModel: PaginationModel
  setPaginationModel: (paginationModel: PaginationModel) => void
  validationErrors: BatchFieldErrors | undefined
}

const UploadEntityDataGrid = ({
  data,
  fields,
  referencedEntities,
  referencedTypes,
  paginationModel,
  setPaginationModel,
  validationErrors,
}: EntityDataGridParams) => (
  <PagedTable
    data-testid="import-entities-grid"
    columns={getColumns(fields, referencedEntities, referencedTypes)}
    rows={getRows(data, fields, validationErrors)}
    paginationModel={paginationModel}
    onPaginationModelChange={setPaginationModel}
  />
)

export default UploadEntityDataGrid
